import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass, createBlock as _createBlock, normalizeStyle as _normalizeStyle, Fragment as _Fragment, createVNode as _createVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, withKeys as _withKeys, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = {
  key: 0,
  class: "ui-select__required"
}
const _hoisted_3 = { class: "ui-select__container" }
const _hoisted_4 = ["id", "tabindex", "onKeydown"]
const _hoisted_5 = {
  key: 1,
  class: "ui-select__value"
}
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 2,
  class: "ui-select__placeholder"
}
const _hoisted_8 = {
  key: 3,
  class: "ui-select__spinner"
}
const _hoisted_9 = {
  key: 0,
  class: "ui-select__search"
}
const _hoisted_10 = ["placeholder"]
const _hoisted_11 = { class: "ui-select__options-list" }
const _hoisted_12 = {
  key: 0,
  class: "ui-select__group-header"
}
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = { class: "ui-select__option-label" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 0,
  class: "ui-select__info-tooltip"
}
const _hoisted_18 = {
  key: 0,
  class: "ui-select__option-actions"
}
const _hoisted_19 = ["onClick"]
const _hoisted_20 = ["onClick"]
const _hoisted_21 = {
  key: 0,
  class: "ui-select__no-results"
}
const _hoisted_22 = {
  key: 1,
  class: "ui-select__footer"
}
const _hoisted_23 = {
  key: 1,
  class: "ui-select__helper"
}
const _hoisted_24 = {
  key: 0,
  class: "ui-select__error"
}
const _hoisted_25 = {
  key: 1,
  class: "ui-select__helper-text"
}

import { Icon } from "@iconify/vue";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { SelectOption, Size } from "./types";

interface Props {
  modelValue?: any;
  label?: string;
  placeholder?: string;
  options: SelectOption[] | any[];
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  leadingIcon?: string;
  leadingIconClass?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  clearable?: boolean;
  labelKey?: string;
  valueKey?: string;
  returnObject?: boolean;
  formatter?: Function;
  showActions?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  showAddButton?: boolean;
  addButtonText?: string;
  loading?: boolean;
  infoTooltipTrigger?: "click" | "hover";
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Select',
  props: {
    modelValue: {},
    label: {},
    placeholder: {},
    options: {},
    size: { default: "md" },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    error: {},
    helperText: {},
    id: {},
    leadingIcon: {},
    leadingIconClass: {},
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { default: "Search..." },
    noResultsText: { default: "No results found" },
    clearable: { type: Boolean, default: false },
    labelKey: {},
    valueKey: {},
    returnObject: { type: Boolean, default: false },
    formatter: {},
    showActions: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: true },
    canDelete: { type: Boolean, default: true },
    showAddButton: { type: Boolean, default: false },
    addButtonText: { default: "Add New" },
    loading: { type: Boolean, default: false },
    infoTooltipTrigger: { default: "click" }
  },
  emits: ["update:modelValue", "blur", "focus", "change", "edit", "delete", "add-new"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isFocused = ref(false);
const isOpen = ref(false);
const isMounted = ref(false);
const selectWrapper = ref<HTMLElement | null>(null);
const dropdownMenu = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});
const searchQuery = ref("");
const activeInfoTooltip = ref<any>(null);

const toggleInfoTooltip = (value: any) => {
  activeInfoTooltip.value = activeInfoTooltip.value === value ? null : value;
};

const selectId = computed(() => props.id || `select-${Math.random().toString(36).substr(2, 9)}`);

// Normalize options: handles both {value, label} arrays and raw object arrays with labelKey/valueKey
const normalizedOptions = computed<(SelectOption & { _raw?: any })[]>(() => {
  const opts = Array.isArray(props.options) ? props.options : [];
  if (!opts.length) return [];

  // If labelKey/valueKey are provided, map raw objects
  if (props.labelKey && props.valueKey) {
    return opts.map((opt) => {
      if (typeof opt === "object" && opt !== null) {
        return {
          value: opt[props.valueKey!],
          label: String(opt[props.labelKey!] ?? ""),
          disabled: opt.disabled,
          icon: opt.icon,
          iconColor: opt.iconColor,
          group: opt.group,
          description: opt.description,
          _raw: opt,
        };
      }
      return { value: opt, label: String(opt), _raw: opt };
    });
  }

  // Standard SelectOption[] — pass through
  return opts.map((opt) => ({ ...opt, _raw: opt }));
});

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return normalizedOptions.value;
  }
  const query = searchQuery.value.toLowerCase();
  return normalizedOptions.value.filter((option) => option.label.toLowerCase().includes(query));
});

// Build grouped items: inserts group headers before each new group
const groupedItems = computed(() => {
  const items: any[] = [];
  let lastGroup: string | undefined = undefined;
  for (const opt of filteredOptions.value) {
    const group = opt.group || opt._raw?.group;
    if (group && group !== lastGroup) {
      items.push({ _groupHeader: true, group });
      lastGroup = group;
    }
    items.push(opt);
  }
  return items;
});

// Find the selected normalized option
const selectedNormalized = computed(() => {
  const mv = props.modelValue;
  if (mv == null) return null;

  // If returnObject or modelValue is an object, match by valueKey or value
  if (typeof mv === "object" && mv !== null && props.valueKey) {
    const mvVal = mv[props.valueKey];
    return normalizedOptions.value.find((opt) => opt.value === mvVal) || null;
  }

  return normalizedOptions.value.find((opt) => opt.value === mv) || null;
});

// The raw object for the selected option (for formatter)
const selectedRaw = computed(() => {
  return selectedNormalized.value?._raw || props.modelValue;
});

// Display text for the selected option
const displayValue = computed(() => {
  if (selectedNormalized.value) return selectedNormalized.value.label;
  // If modelValue is an object and we have a labelKey, read it directly
  if (props.modelValue && typeof props.modelValue === "object" && props.labelKey) {
    return props.modelValue[props.labelKey] || null;
  }
  return null;
});

const wrapperClasses = computed(() => {
  const classes = ["ui-select"];
  classes.push(`ui-select--${props.size}`);
  if (props.disabled) classes.push("ui-select--disabled");
  if (props.error) classes.push("ui-select--error");
  return classes;
});

const selectClasses = computed(() => {
  const classes = ["ui-select__field"];
  if (isFocused.value || isOpen.value) classes.push("ui-select__field--focused");
  if (isOpen.value) classes.push("ui-select__field--open");
  if (props.disabled) classes.push("ui-select__field--disabled");
  if (props.leadingIcon) classes.push("ui-select__field--with-leading-icon");
  return classes;
});

const isSelected = (option: SelectOption & { _raw?: any }) => {
  const mv = props.modelValue;
  if (mv == null) return false;

  // Object modelValue — match by valueKey
  if (typeof mv === "object" && mv !== null && props.valueKey) {
    return option.value === mv[props.valueKey!];
  }

  return option.value === mv;
};

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
      if (props.searchable && searchInput.value) {
        searchInput.value.focus();
      }
    });
  }
};

const handleSelectOption = (option: SelectOption & { _raw?: any }) => {
  if (option.disabled) return;
  const emitValue = props.returnObject ? option._raw || option : option.value;
  emit("update:modelValue", emitValue);
  emit("change", emitValue);
  isOpen.value = false;
  activeInfoTooltip.value = null;
};

const clearValue = () => {
  emit("update:modelValue", null);
  emit("change", null);
};

const updateDropdownPosition = () => {
  if (!selectWrapper.value || !dropdownMenu.value) return;

  try {
    const rect = selectWrapper.value.getBoundingClientRect();
    if (!rect) return;

    const spacing = 4;
    const dropdownHeight = dropdownMenu.value.offsetHeight || 200;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

    dropdownStyle.value = {
      position: "fixed",
      top: shouldShowAbove
        ? `${rect.top - dropdownHeight - spacing}px`
        : `${rect.bottom + spacing}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`,
      width: "auto",
      zIndex: "10090",
    };
  } catch (error) {
    console.warn("Error updating dropdown position:", error);
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    selectWrapper.value &&
    !selectWrapper.value.contains(event.target as Node) &&
    dropdownMenu.value &&
    !dropdownMenu.value.contains(event.target as Node)
  ) {
    isOpen.value = false;
  }
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

watch(isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      if (selectWrapper.value && dropdownMenu.value) {
        updateDropdownPosition();
      }
    });
  } else {
    searchQuery.value = "";
  }
});

onMounted(() => {
  nextTick(() => {
    if (typeof document !== "undefined" && document.body) {
      isMounted.value = true;
    }
  });
  document.addEventListener("mousedown", handleClickOutside);
  window.addEventListener(
    "scroll",
    () => {
      if (isOpen.value && selectWrapper.value && dropdownMenu.value) {
        updateDropdownPosition();
      }
    },
    true
  );
  window.addEventListener("resize", () => {
    if (isOpen.value && selectWrapper.value && dropdownMenu.value) {
      updateDropdownPosition();
    }
  });
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(wrapperClasses.value),
    ref_key: "selectWrapper",
    ref: selectWrapper
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: selectId.value,
          class: "ui-select__label"
        }, [
          _createTextVNode(_toDisplayString(__props.label) + " ", 1),
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        id: selectId.value,
        class: _normalizeClass(selectClasses.value),
        tabindex: __props.disabled ? -1 : 0,
        onClick: toggleDropdown,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onKeydown: [
          _withKeys(_withModifiers(toggleDropdown, ["prevent"]), ["enter"]),
          _withKeys(_withModifiers(toggleDropdown, ["prevent"]), ["space"]),
          _cache[0] || (_cache[0] = _withKeys(($event: any) => (isOpen.value = false), ["esc"]))
        ]
      }, [
        (__props.leadingIcon)
          ? (_openBlock(), _createBlock(_unref(Icon), {
              key: 0,
              icon: __props.leadingIcon,
              class: _normalizeClass(['ui-select__leading-icon', __props.leadingIconClass])
            }, null, 8, ["icon", "class"]))
          : _createCommentVNode("", true),
        (displayValue.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
              (__props.formatter)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    innerHTML: __props.formatter(selectedRaw.value)
                  }, null, 8, _hoisted_6))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (selectedNormalized.value?.icon)
                      ? (_openBlock(), _createBlock(_unref(Icon), {
                          key: 0,
                          icon: selectedNormalized.value.icon,
                          style: _normalizeStyle(selectedNormalized.value.iconColor ? { color: selectedNormalized.value.iconColor } : {}),
                          class: "ui-select__value-icon"
                        }, null, 8, ["icon", "style"]))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(displayValue.value), 1)
                  ], 64))
            ]))
          : (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(__props.placeholder), 1)),
        (__props.loading)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(_unref(Icon), {
                icon: "mdi:loading",
                class: "ui-select__spinner-icon"
              })
            ]))
          : _createCommentVNode("", true),
        (__props.clearable && displayValue.value && !__props.disabled)
          ? (_openBlock(), _createElementBlock("button", {
              key: 4,
              type: "button",
              class: "ui-select__clear",
              onClick: _withModifiers(clearValue, ["stop"]),
              "aria-label": "Clear selection"
            }, [
              _createVNode(_unref(Icon), { icon: "mdi:close-circle" })
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: _normalizeClass(["ui-select__icon", { 'is-open': isOpen.value }])
        }, [
          _createVNode(_unref(Icon), { icon: "mdi:chevron-down" })
        ], 2)
      ], 42, _hoisted_4),
      (isMounted.value)
        ? (_openBlock(), _createBlock(_Teleport, {
            key: 0,
            to: "body"
          }, [
            _createVNode(_Transition, { name: "dropdown" }, {
              default: _withCtx(() => [
                (isOpen.value && (normalizedOptions.value.length > 0 || __props.searchable))
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      ref_key: "dropdownMenu",
                      ref: dropdownMenu,
                      class: "ui-select__dropdown",
                      style: _normalizeStyle(dropdownStyle.value)
                    }, [
                      (__props.searchable)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:magnify",
                              class: "ui-select__search-icon"
                            }),
                            _withDirectives(_createElementVNode("input", {
                              ref_key: "searchInput",
                              ref: searchInput,
                              type: "text",
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
                              placeholder: __props.searchPlaceholder,
                              class: "ui-select__search-input",
                              onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                            }, null, 8, _hoisted_10), [
                              [_vModelText, searchQuery.value]
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_11, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedItems.value, (item) => {
                          return (_openBlock(), _createElementBlock(_Fragment, {
                            key: item._groupHeader ? `header-${item.group}` : item.value
                          }, [
                            (item._groupHeader)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(item.group), 1))
                              : (_openBlock(), _createElementBlock("div", {
                                  key: 1,
                                  class: _normalizeClass(["ui-select__option", { 'is-selected': isSelected(item), 'is-disabled': item.disabled }])
                                }, [
                                  _createElementVNode("div", {
                                    class: "ui-select__option-content",
                                    onClick: ($event: any) => (handleSelectOption(item))
                                  }, [
                                    (__props.formatter)
                                      ? (_openBlock(), _createElementBlock("span", {
                                          key: 0,
                                          class: "ui-select__option-label",
                                          innerHTML: __props.formatter(item._raw || item)
                                        }, null, 8, _hoisted_14))
                                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                          (item.icon)
                                            ? (_openBlock(), _createBlock(_unref(Icon), {
                                                key: 0,
                                                icon: item.icon,
                                                style: _normalizeStyle(item.iconColor ? { color: item.iconColor } : {}),
                                                class: "ui-select__option-icon"
                                              }, null, 8, ["icon", "style"]))
                                            : _createCommentVNode("", true),
                                          _createElementVNode("span", _hoisted_15, _toDisplayString(item.label), 1)
                                        ], 64)),
                                    (item.description)
                                      ? (_openBlock(), _createElementBlock("span", {
                                          key: 2,
                                          class: _normalizeClass(["ui-select__info-icon", { 'ui-select__info-icon--hover': __props.infoTooltipTrigger === 'hover' }]),
                                          onClick: _withModifiers(($event: any) => (
                        __props.infoTooltipTrigger === 'click' ? toggleInfoTooltip(item.value) : undefined
                      ), ["stop"])
                                        }, [
                                          _createVNode(_unref(Icon), {
                                            icon: "mdi:information-outline",
                                            width: "14",
                                            height: "14"
                                          }),
                                          (__props.infoTooltipTrigger === 'hover' || activeInfoTooltip.value === item.value)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(item.description), 1))
                                            : _createCommentVNode("", true)
                                        ], 10, _hoisted_16))
                                      : _createCommentVNode("", true),
                                    (isSelected(item))
                                      ? (_openBlock(), _createBlock(_unref(Icon), {
                                          key: 3,
                                          icon: "mdi:check",
                                          class: "ui-select__check-icon"
                                        }))
                                      : _createCommentVNode("", true)
                                  ], 8, _hoisted_13),
                                  (__props.showActions)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                        (__props.canEdit)
                                          ? (_openBlock(), _createElementBlock("button", {
                                              key: 0,
                                              onClick: _withModifiers(($event: any) => {
                        isOpen.value = false;
                        emit('edit', item._raw || item);
                      }, ["stop"]),
                                              class: "ui-select__action-btn ui-select__action-btn--edit",
                                              type: "button",
                                              "aria-label": "Edit"
                                            }, [
                                              _createVNode(_unref(Icon), {
                                                icon: "mdi:pencil-outline",
                                                width: "14",
                                                height: "14"
                                              })
                                            ], 8, _hoisted_19))
                                          : _createCommentVNode("", true),
                                        (__props.canDelete)
                                          ? (_openBlock(), _createElementBlock("button", {
                                              key: 1,
                                              onClick: _withModifiers(($event: any) => {
                        isOpen.value = false;
                        emit('delete', item._raw || item);
                      }, ["stop"]),
                                              class: "ui-select__action-btn ui-select__action-btn--delete",
                                              type: "button",
                                              "aria-label": "Delete"
                                            }, [
                                              _createVNode(_unref(Icon), {
                                                icon: "mdi:trash-can-outline",
                                                width: "14",
                                                height: "14"
                                              })
                                            ], 8, _hoisted_20))
                                          : _createCommentVNode("", true)
                                      ]))
                                    : _createCommentVNode("", true)
                                ], 2))
                          ], 64))
                        }), 128)),
                        (filteredOptions.value.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(__props.noResultsText), 1))
                          : _createCommentVNode("", true)
                      ]),
                      (__props.showAddButton)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                            _createElementVNode("button", {
                              onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (emit('add-new')), ["stop"])),
                              class: "ui-select__add-btn",
                              type: "button",
                              "aria-label": "Add new"
                            }, [
                              _createVNode(_unref(Icon), {
                                icon: "mdi:plus",
                                width: "14",
                                height: "14"
                              }),
                              _createElementVNode("span", null, _toDisplayString(__props.addButtonText), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ], 4))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    (__props.error || __props.helperText)
      ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
          (__props.error)
            ? (_openBlock(), _createElementBlock("span", _hoisted_24, _toDisplayString(__props.error), 1))
            : (__props.helperText)
              ? (_openBlock(), _createElementBlock("span", _hoisted_25, _toDisplayString(__props.helperText), 1))
              : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})